import React, {
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
  useCallback,
} from 'react';

/**
 * ResponsiveBadgeList dynamically calculates how many complete badges can fit
 * horizontally inside the container's available width without wrapping or clipping.
 * When items exceed available width, it accurately reserves space for the "+N" badge
 * so that both the visible badges and the "+N" badge are 100% visible on a single line.
 */
export function ResponsiveBadgeList({
  items = [],
  renderItem,
  renderMore,
  gap = 4,
  max,
  className = '',
  style = {},
  emptyState = null,
}) {
  const containerRef = useRef(null);
  const measureContainerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(() => {
    return typeof max === 'number' && max >= 0
      ? Math.min((items || []).length, max)
      : (items || []).length;
  });
  const visibleCountRef = useRef(visibleCount);
  visibleCountRef.current = visibleCount;

  // Stable signature for items to detect changes
  const itemsSignature = Array.isArray(items)
    ? items
        .map((item) =>
          typeof item === 'object' && item !== null
            ? (item.id ?? item.name ?? JSON.stringify(item))
            : String(item),
        )
        .join('__')
    : '';

  const calculateFit = useCallback(() => {
    const container = containerRef.current;
    const measureContainer = measureContainerRef.current;
    if (!container || !measureContainer || items.length === 0) return;

    // Use getBoundingClientRect for sub-pixel accuracy
    // The chip row can grow with its content. Measuring that width makes
    // every chip "fit", then an ancestor clips the row and the next cell.
    const parent = container.parentElement;
    let availableWidth = Math.floor(container.clientWidth || 0);
    if (parent) {
      const parentStyle = getComputedStyle(parent);
      const parentInner =
        parent.clientWidth -
        (parseFloat(parentStyle.paddingLeft) || 0) -
        (parseFloat(parentStyle.paddingRight) || 0);
      if (parentInner > 0) {
        availableWidth =
          availableWidth > 0
            ? Math.min(availableWidth, Math.floor(parentInner))
            : Math.floor(parentInner);
      }
    }
    if (availableWidth <= 0) return;

    const itemNodes = measureContainer.querySelectorAll('[data-measure-item]');
    if (!itemNodes || itemNodes.length === 0) return;

    const itemWidths = [];
    for (let i = 0; i < itemNodes.length; i++) {
      itemWidths.push(itemNodes[i].getBoundingClientRect().width);
    }

    const moreNode1 = measureContainer.querySelector('[data-measure-more-1]');
    const moreNodeN = measureContainer.querySelector('[data-measure-more-n]');

    const getMoreWidth = (hiddenCount) => {
      if (hiddenCount <= 1 && moreNode1) {
        return moreNode1.getBoundingClientRect().width;
      }
      if (moreNodeN) {
        return moreNodeN.getBoundingClientRect().width;
      }
      return moreNode1 ? moreNode1.getBoundingClientRect().width : 0;
    };

    const maxLimit = typeof max === 'number' && max >= 0 ? max : items.length;

    // 1. If all items fit without "+N" badge (and items.length <= maxLimit)
    if (items.length <= maxLimit) {
      const totalAllWidth =
        itemWidths.reduce((sum, w) => sum + w, 0) + (items.length - 1) * gap;
      if (Math.ceil(totalAllWidth) <= availableWidth) {
        if (visibleCountRef.current !== items.length) {
          visibleCountRef.current = items.length;
          setVisibleCount(items.length);
        }
        return;
      }
    }

    // 2. Otherwise, we MUST reserve space for "+N" badge
    // We start from Math.min(items.length - 1, maxLimit) down to 1
    const startK = Math.min(items.length - 1, maxLimit);
    let targetCount = 0;

    for (let k = startK; k >= 1; k--) {
      const hiddenCount = items.length - k;
      const moreWidth = getMoreWidth(hiddenCount);
      // k items + 1 more badge => (k + 1) elements, meaning k gaps
      const itemsWidth = itemWidths.slice(0, k).reduce((sum, w) => sum + w, 0);
      const totalNeeded = itemsWidth + k * gap + moreWidth;

      if (Math.ceil(totalNeeded) <= availableWidth) {
        targetCount = k;
        break;
      }
    }

    // 3. If targetCount is 0, even 1 item + "+N" badge cannot fit.
    // targetCount = 0 renders only the "+N" badge (e.g. [+3]).
    if (visibleCountRef.current !== targetCount) {
      visibleCountRef.current = targetCount;
      setVisibleCount(targetCount);
    }
  }, [items.length, gap, max]);

  // Synchronous layout calculation before paint to eliminate any flicker
  useLayoutEffect(() => {
    calculateFit();
  }, [calculateFit, itemsSignature]);

  // Keep a stable ref to calculateFit so ResizeObserver callback is always up to date
  const calculateFitRef = useRef(calculateFit);
  calculateFitRef.current = calculateFit;

  // ResizeObserver observes changes to container width (window resize, layout change, etc.)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let rafId = null;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        calculateFitRef.current();
      });
    });

    observer.observe(container);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, []);

  if (!items || items.length === 0) {
    return emptyState;
  }

  const visibleItems = items.slice(0, visibleCount);
  const hiddenItems = items.slice(visibleCount);
  const hiddenCount = items.length - visibleCount;

  return (
    <>
      {/* Hidden off-screen measurement elements (takes 0 layout space, causes zero scrollbars) */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          overflow: 'hidden',
          visibility: 'hidden',
          pointerEvents: 'none',
          zIndex: -9999,
          opacity: 0,
        }}
      >
        <div
          ref={measureContainerRef}
          style={{
            display: 'inline-flex',
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
            gap: `${gap}px`,
          }}
        >
          {items.map((item, idx) => (
            <div
              key={idx}
              data-measure-item={idx}
              style={{
                display: 'inline-flex',
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              {renderItem(item, idx)}
            </div>
          ))}
          {/* Representative +1 badge for measurement */}
          <div
            data-measure-more-1
            style={{
              display: 'inline-flex',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {renderMore(1, items.slice(items.length - 1))}
          </div>
          {/* Representative +N badge for measurement */}
          <div
            data-measure-more-n
            style={{
              display: 'inline-flex',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {renderMore(items.length, items)}
          </div>
        </div>
      </div>

      {/* Visible badges container */}
      <div
        ref={containerRef}
        className={`flex items-center flex-nowrap min-w-0 max-w-full ${className}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          whiteSpace: 'nowrap',
          gap: `${gap}px`,
          minWidth: 0,
          maxWidth: '100%',
          width: '100%',
          ...style,
        }}
      >
        {visibleItems.map((item, idx) => (
          <React.Fragment key={idx}>{renderItem(item, idx)}</React.Fragment>
        ))}
        {hiddenCount > 0 && renderMore(hiddenCount, hiddenItems)}
      </div>
    </>
  );
}
